import { SLTXFormatRootFile } from '../../fileFormats/sltx/sltx';
import { ModelGroupInfo } from '../../fileFormats/sltx/modelGroupInfo';

import {
  ModelTreeNode,
  ModelTreeNodeDataOption,
  ParsedTreeNodeType,
} from '../data/modelOptionsTree';

import {
  TreeOptionSelection,
  TreeOptionSelections,
} from '../data/optionsSelection';

import {
  TreeSelections,
} from '../data/treeSelection';

import { TreeStateInfo } from './treeStateInfo';
import { TreeManipulation } from './treeManipulation';

// class TreeOptionsSelected
//
// The purpose of this class is to check and fix user selections.
// When the user selects an option, and later, change a group or domain, that
// causes the current option to become unavailable, we need to fix the selection.
// This is done by looking at the name of that previous option, and try to find
// an option with the same name in the new group/domain.

export class TreeOptionsSelected {

  public static ensureUserSelectionIsValid(
    tree : ModelTreeNode,
    optionSelections: TreeOptionSelections,
    treeSelections: TreeSelections,
    sltxFile : SLTXFormatRootFile,
    updateOptionSelection: (groupID: number, optionID: number | undefined) => void,
  ) :boolean {

    let changeMade = false;

    // Check all the selections
    for (let i = 0; i < optionSelections.length; i += 1) {

      const optionSelection = optionSelections[i];

      // If the selection is not valid, then fix it
      if (!TreeOptionsSelected.isSelectionValid(
        tree,
        optionSelection,
        treeSelections,
      )) {

        TreeOptionsSelected.fixInvalidSelection(
          tree,
          optionSelection,
          treeSelections,
          sltxFile,
          updateOptionSelection,
        );

        changeMade = true;
      }
    }

    return changeMade;
  }

  public static isSelectionValid(
    tree : ModelTreeNode,
    optionSelection: TreeOptionSelection,
    treeSelections: TreeSelections,
  ): boolean {

    // Locate the node in the tree that represents the group option
    const optionNode = TreeManipulation.findOptionNodeInTree(
      tree,
      optionSelection.groupID,
      optionSelection.optionID,
    );

    // A selection will be considered valid if the nodes from the root to this node, are
    // all available and selected
    if (optionNode === undefined || optionNode.node === undefined) {
      return false;
    }

    const canOptionBeSelected = TreeStateInfo.canOptionNodeBeSelected(
      optionNode.node.path,
      tree,
      treeSelections,
    );

    return canOptionBeSelected;
  }

  public static fixInvalidSelection = (
    tree : ModelTreeNode,
    groupSelection: TreeOptionSelection,
    treeSelections : TreeSelections,
    sltxFile : SLTXFormatRootFile,
    updateOptionSelection: (groupID: number, optionID: number | undefined) => void,
  ) => {

    // Grab the group from the sltx file
    const group = ModelGroupInfo.getGroup(sltxFile, groupSelection.groupID);
    if (group === undefined) {
      return;
    }

    // Grab the current option from the group
    const currentOption = ModelGroupInfo.getOption(
      sltxFile,
      groupSelection.groupID,
      groupSelection.optionID,
    );

    if (currentOption === undefined) {
      return;
    }

    // Grab the last part of the option name
    const optionNameLastPart = currentOption.name;

    // Grab all the nodes in the tree that are options for this group
    const groupOptionsInTree = TreeManipulation.filterNodes(
      tree,
      (node) => {
        if (node.type !== ParsedTreeNodeType.Option) {
          return false;
        }

        if (TreeManipulation.getGroupIDFromOptionNode(node) !== group.id) {
          return false;
        }

        return true;
      },
    );

    if (groupOptionsInTree.length === 0) {
      updateOptionSelection(groupSelection.groupID, undefined);
      return;
    }

    let newOptionToSelect : ModelTreeNode | undefined;

    // Go through the available nodes and check if there's an option there with the same name as
    // the current option that can be selected
    newOptionToSelect = groupOptionsInTree.find((groupOptionInTree) => {

      const nameIsMatching = groupOptionInTree.name === optionNameLastPart;

      const canOptionBeSelected = TreeStateInfo.canOptionNodeBeSelected(
        groupOptionInTree.path,
        tree,
        treeSelections,
      );

      if (nameIsMatching && canOptionBeSelected) {
        return true;
      }

      return false;
    });

    if (newOptionToSelect === undefined) {

      // Selecting the first available option because we couldn't find an option
      // with a similar name

      // Go through the options, and pick the first one that is selectable
      for (let curOption = 0; curOption < groupOptionsInTree.length; curOption += 1) {

        const possibleOption = groupOptionsInTree[curOption];
        const canOptionBeSelected = TreeStateInfo.canOptionNodeBeSelected(
          possibleOption.path,
          tree,
          treeSelections,
        );

        if (canOptionBeSelected) {
          newOptionToSelect = possibleOption;
          break;
        }
      }
    }

    if (newOptionToSelect === undefined) {

      // Forced to pick the first option, because we couldn't find any option to select
      // If we could not find any option to select, then pick the first one

      // eslint-disable-next-line prefer-destructuring
      newOptionToSelect = groupOptionsInTree[0];
    }

    if (newOptionToSelect === undefined) {
      // We couldn't find any suitable option to select for group
      return;
    }

    updateOptionSelection(groupSelection.groupID, newOptionToSelect.id);
  };

  public static applyOptionsSelectionsToTreeSelections(
    groupSelections: TreeOptionSelections,
    treeSelectionState : TreeSelections,
    tree : ModelTreeNode,
    setTreeSelectionState: (treeSelectionState: TreeSelections) => void,
  ) : boolean {

    let changesMade = false;

    const newTreeSelections = [...treeSelectionState];

    // Go through all the group selections
    groupSelections.forEach((groupSelection) => {

      // Find the option Node, that is representing this selection
      const treeOptionNodeFindResult = TreeManipulation.findOptionNodeInTree(
        tree,
        groupSelection.groupID,
        groupSelection.optionID,
      );

      if (treeOptionNodeFindResult === undefined || treeOptionNodeFindResult.node === undefined) {
        return;
      }

      changesMade = this.addFullPathSelectionToTree(
        tree,
        treeOptionNodeFindResult.node.path,
        newTreeSelections,
      );
    });

    setTreeSelectionState(newTreeSelections);

    return changesMade;
  }

  private static addFullPathSelectionToTree(
    tree: ModelTreeNode,
    targetPath : string,
    treeSelections : TreeSelections,
  ) : boolean {

    let newTreeSelections = [...treeSelections];

    let changesMade = false;

    // Split the path into parts
    const targetPathParts = targetPath.split('/');

    while (targetPathParts.length > 1) {

      // Get all the parts except the last
      const parentPathParts = targetPathParts.slice(0, targetPathParts.length - 1);
      const parentPath = parentPathParts.join('/');

      // Build the path to the parent
      const nodePath = targetPathParts.join('/');

      // Locate the node with this path
      const nodeFindResult = TreeManipulation.findNodeInTree(
        tree,
        undefined,
        undefined,
        (n) => n.path === nodePath,
      );

      if (nodeFindResult === undefined || nodeFindResult.node === undefined) {
        // Update targetPathParts
        targetPathParts.pop();

        // eslint-disable-next-line no-continue
        continue;
      }

      if (nodeFindResult.node.type === ParsedTreeNodeType.Group
        || nodeFindResult.node.type === ParsedTreeNodeType.Domain) {

        // This node is not selectable, so it will never have a selection

        // Update targetPathParts
        targetPathParts.pop();

        // eslint-disable-next-line no-continue
        continue;
      }

      // Check if the parent is already in the tree selections
      const parentAlreadyInTreeSelections = treeSelections.find(
        (treeSelection) => treeSelection.path === parentPath,
      );

      let addEntry = false;

      if (parentAlreadyInTreeSelections !== undefined) {
        if (parentAlreadyInTreeSelections.value !== nodePath) {

          // There is an entry, but have the wrong value. Remove it
          newTreeSelections = newTreeSelections.filter(
            (treeSelection) => treeSelection.path !== parentPath,
          );

          // Indicate that we need to add the entry
          addEntry = true;
        }
      } else {
        // We don't have this entry. Add it
        addEntry = true;
      }

      // Check if we need to update it
      if (addEntry) {

        changesMade = true;

        newTreeSelections.push({
          path: parentPath,
          value: nodePath,
          type: nodeFindResult.node.type,
        });
      }

      // Update targetPathParts
      targetPathParts.pop();
    }

    // Put the contents of newTreeSelections into treeSelections
    treeSelections.splice(0, treeSelections.length, ...newTreeSelections);

    return changesMade;
  }

  public static addDefaultSelectionIfNeeded(
    node : ModelTreeNode,
    tree : ModelTreeNode,
    groupSelections: TreeOptionSelections,
    treeSelectionState : TreeSelections,
    updateOptionSelection: (groupID: number, optionID: number | undefined) => void,
    setTreeSelectionState: (treeSelectionState: TreeSelections) => void,
  ) : boolean {

    // We need to check the types of our children and decide if we need to
    // select any of them
    // If one of them is selected, call this function on them
    // If they are not to be selected, call this function on all of them
    // If they are options, don't call this function on any of them

    // Determine the type of children we have

    // First of all, check if we have children
    // If we don't, we're done.

    if (node.children.length === 0) {
      // Indicate no change was made
      return false;
    }

    // Grab the current selection for this node
    const currentNodeSelection = treeSelectionState.find(
      (treeSelection) => treeSelection.path === node.path,
    );

    // Get the first child
    const firstChild = node.children[0];

    // Get the type of the child
    const childType = firstChild.type;

    switch (childType) {
      case ParsedTreeNodeType.Group:
      case ParsedTreeNodeType.Domain:

      {
        // This type of children are not selectable
        // We should call this function to all of them

        // Go through the children
        for (let i = 0; i < node.children.length; i += 1) {

          const child = node.children[i];

          const changesMade = this.addDefaultSelectionIfNeeded(
            child,
            tree,
            groupSelections,
            treeSelectionState,
            updateOptionSelection,
            setTreeSelectionState,
          );

          if (changesMade) {
            return true;
          }
        }

        // Indicate no change was made
        return false;
      }

      case ParsedTreeNodeType.Folder:
      case ParsedTreeNodeType.DomainGroup:
      {
        // This type of children is selectable
        // Check if we have a selection already

        if (currentNodeSelection === undefined) {

          // Select the first folder available

          const newTreeSelections = [...treeSelectionState, {
            path: node.path,
            value: firstChild.path,
            type: node.type,
          }];

          setTreeSelectionState(newTreeSelections);

          // Indicate that there's a change
          return true;
        }

        // We already have a selection
        // Just call this function on that child

        // Find the child
        const selectedChildNode = TreeManipulation.findNodeInTree(
          tree,
          undefined,
          undefined,
          (n) => n.path === currentNodeSelection.value,
        );

        // If we cannot find the child, that's probably an issue
        if (selectedChildNode === undefined || selectedChildNode.node === undefined) {

          // Remove the selection
          const newTreeSelections = treeSelectionState.filter(
            (treeSelection) => treeSelection.path !== node.path,
          );

          setTreeSelectionState(newTreeSelections);

          // Indicate that there's a change
          return true;
        }

        // This child was found
        // Go deep and make that child pick a default selection

        const changesMade = this.addDefaultSelectionIfNeeded(
          selectedChildNode.node,
          tree,
          groupSelections,
          treeSelectionState,
          updateOptionSelection,
          setTreeSelectionState,
        );

        if (changesMade === true) {
          return true;
        }
        break;
      }

      case ParsedTreeNodeType.Option:
      {
        const groupID = TreeManipulation.getGroupIDFromOptionNode(firstChild);

        const existingOptionSelection = groupSelections.find(
          (groupSelection) => groupSelection.groupID === groupID,
        );

        let needNewSelection = false;

        if (existingOptionSelection !== undefined) {

          // Locate the node in the tree that represents the group option
          const optionNode = TreeManipulation.findOptionNodeInTree(
            tree,
            existingOptionSelection.groupID,
            existingOptionSelection.optionID,
          );

          if (optionNode === undefined) {
            needNewSelection = true;
          } else {
            let canOptionBeSelected = false;

            if (optionNode.node) {
              // Check if current selection is valid
              canOptionBeSelected = TreeStateInfo.canOptionNodeBeSelected(
                optionNode.node.path,
                tree,
                treeSelectionState,
              );
            }
            if (canOptionBeSelected === false) {
              needNewSelection = true;
            }
          }
        } else {
          needNewSelection = true;
        }

        if (currentNodeSelection === undefined && needNewSelection) {

          const optionData = firstChild.data as ModelTreeNodeDataOption;

          // Select the first option available
          updateOptionSelection(
            optionData.groupID,
            firstChild.id,
          );

          // Indicate that there's a change
          return true;
        }

        if (currentNodeSelection !== undefined) {

          // Find the child
          const selectedChildNode = TreeManipulation.findNodeInTree(
            tree,
            undefined,
            undefined,
            (n) => n.path === currentNodeSelection.value,
          );

          // If we cannot find the child, that's probably an issue
          if (selectedChildNode === undefined || selectedChildNode.node === undefined) {

            // Remove the selection
            updateOptionSelection(
              node.id,
              undefined,
            );

            // Indicate that there's a change
            return true;
          }
        }

        // We had a selection and the selection is found
        // Indicate that there's no change
        return false;
      }

      default:
      {
        // Unknown child type
      }
    }

    return false;
  }

  public static logTreeOptionSelections(optionSelections: TreeOptionSelections) : void {

    // Make a copy of the array
    const optionSelectionsCopy = [...optionSelections];

    // Sort the optionSelectionsCopy by the groupID member
    optionSelectionsCopy.sort((a, b) => a.groupID - b.groupID);

    // eslint-disable-next-line no-console
    console.groupCollapsed('TreeOptionSelections');
    for (let i = 0; i < optionSelectionsCopy.length; i += 1) {
      const optionSelection = optionSelectionsCopy[i];
      // eslint-disable-next-line no-console
      console.log(`Group ${optionSelection.groupID}, Option ${optionSelection.optionID}`);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  public static logTreeSelections(treeSelections: TreeSelections) : void {
    // eslint-disable-next-line no-console
    console.groupCollapsed('TreeSelections');
    for (let i = 0; i < treeSelections.length; i += 1) {
      const treeSelection = treeSelections[i];
      // eslint-disable-next-line no-console
      console.log(`${treeSelection.path} > ${treeSelection.value}, ${treeSelection.type}`);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
}
